import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';
import { QueryFactory } from 'services/api';
import { useDocumentLockHub } from 'services/signalR/signalRClients.ts';
import { constantSessionId } from 'helpers/sessionProvider.ts';

const lockChangedDebounceMs = 200;

/**
 * Keeps the editing lock status of a document up to date via SignalR.
 * Unlike useEditingLock, it never locks the document itself.
 * */
export const useDocumentLockStatus = (documentId?: number, enabled = true) => {
  const isEnabled = enabled && !!documentId;

  const queryClient = useQueryClient();
  const lockQuery = QueryFactory.EditingLockQuery.useCheckLockQuery(
    documentId!,
    { enabled: isEnabled },
  );

  const documentIdRef = useRef(documentId);
  documentIdRef.current = documentId;

  const onDocumentLockChanged = useDebouncedCallback(
    (changedDocumentId: number) => {
      void queryClient.invalidateQueries({
        queryKey:
          QueryFactory.EditingLockQuery.checkLockQueryKey(changedDocumentId),
      });
    },
    lockChangedDebounceMs,
  );

  useEffect(
    () => () => onDocumentLockChanged.cancel(),
    [onDocumentLockChanged],
  );

  const { invoker, isConnected } = useDocumentLockHub(
    {
      DocumentLockChanged: (changedDocumentId) => {
        if (changedDocumentId === documentIdRef.current)
          onDocumentLockChanged(changedDocumentId);
      },
    },
    isEnabled,
  );

  useEffect(() => {
    if (!isConnected || !isEnabled) return;

    void invoker.SubscribeToDocumentLocks([documentId!]);

    return () => {
      invoker.UnsubscribeFromDocumentLocks([documentId!]);
    };
  }, [isConnected, isEnabled, documentId]);

  const isLockedByAnotherSession =
    !!lockQuery.data?.isLocked &&
    lockQuery.data.sessionIdentifier !== constantSessionId;

  return { lockQuery, isLockedByAnotherSession };
};
